import { ChangeEvent, FC, InputHTMLAttributes, useEffect, useState, MouseEvent } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import SvgIcon, { SvgIconName } from '~src/shared/SvgIcon';
import HoverInfoIcon from '~src/shared/tooltips/HoverInfoIcon';
const CheckboxContainer = styled.div<{ size?: number }>`
  flex: 0 0 ${({ size }) => size || 0}px;
`;

const LabelSC = styled.label`
  cursor: pointer;
  display: flex;
  align-items: center;

  input {
    display: none;
  }
  span {
    margin-left: 10px;
    &.flipped {
      margin-left: 0;
      margin-right: 10px;
    }
  }

  &.multiline {
    align-items: flex-start;

    img {
      margin-top: 5px;
    }
  }

  &.disabled {
    cursor: default;
    opacity: 0.4;
  }

  .label {
    .hoverIcon {
      display: inline-block;
      margin-left: 10px;
    }
    color: ${({ theme }) => theme.baseBlue.a10};
    user-select: none;
  }
`;

export enum CheckBoxType {
  CheckBox = 'checkbox',
  Radio = 'radio',
}

export enum CheckBoxStatus {
  Empty = 'empty',
  Checked = 'checked',
  Partial = 'partial',
}

interface DefaultProps {

}

interface Props extends Partial<DefaultProps> {
  val?: string;
  labelKey?: string | null;
  label?: string | null;
  multiline?: boolean;
  checkboxSize?: number;
  flipLabel?: boolean;
  disabled?: boolean;
  iconName?: SvgIconName;
  iconPartialName?: SvgIconName;
  id: string;
  input?: InputHTMLAttributes<HTMLInputElement> | null;
  isChecked?: boolean;
  isPartial?: boolean;
  type?: CheckBoxType;
  name?: string;
  onChange?: (checked: boolean, value: string) => void;
  emptyDisabled?: boolean;
  stopPropagation?: boolean;
  help?: string;
  displayIconName?: SvgIconName;
}

const CheckBox: FC<Props> = (props) => {
  const {
    iconPartialName = 'iconCheckboxPartial', id,
    input = null,
    isChecked = false,
    isPartial = false,
    type = CheckBoxType.CheckBox,
    val = '',
    name,
    labelKey = null,
    label = null,
    multiline = false,
    disabled = false,
    checkboxSize = 15,
    help
  } = props;

  const iconNameChecked: SvgIconName =
    props.iconName || type === CheckBoxType.Radio ? 'iconRadio' : 'iconCheckbox';
  const [checked, setChecked] = useState(isChecked);
  const [iconName, setIconName] = useState<SvgIconName>(iconNameChecked);

  useEffect(() => {
    setChecked(isChecked);
    if (isPartial && iconPartialName) {
      setIconName(iconPartialName);
    } else {
      setIconName(iconNameChecked);
    }
  }, [isPartial, isChecked]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!disabled && !disabled) {
      setChecked(e.target.checked);
      if (props.onChange) {
        props.onChange(e.target.checked, e.target.value);
      }
    }
  };

  const onComponentClick = (e: MouseEvent<HTMLLabelElement>) => {
    if (props.stopPropagation) {
      e.stopPropagation();
    }
  };

  return (
    <LabelSC onClick={onComponentClick} htmlFor={id} className={classNames({ multiline, disabled })}>
      {label && props.flipLabel && (
        <span className="label flipped" data-translation={labelKey}>
          {label}
          {help && <HoverInfoIcon infoText={help} />}
        </span>
      )}
      <CheckboxContainer size={checkboxSize}>
        <SvgIcon className={classNames({ unchecked: !checked })} name={iconName} width={checkboxSize} />
      </CheckboxContainer>
      <input
        id={id}
        type={type}
        name={name}
        value={val}
        onChange={handleChange}
        checked={checked}
        disabled={disabled || props.emptyDisabled}
        {...input}
      />
     {props.displayIconName && <div style={{marginLeft: "8px"}}><SvgIcon name={props.displayIconName} className="subject-icon" /></div>}
      {label && !props.flipLabel && (
        <span className="label" data-translation={labelKey}>
          {label}
          {help && <HoverInfoIcon infoText={help} />}
        </span>
      )}
    </LabelSC>
  );
};

export default CheckBox;
